/*
 * Solutions page (templates/page.solutions.json)
 *  - Hero: background glow follows the pointer
 *  - Groups section: switch groups (tabs mode) or follow scroll (stacked mode)
 *  - App review carousels: autoplay, dots, pause on hover/focus
 *  - Big number count-up when scrolled into view
 * Re-initialises on Theme Editor section reloads.
 */
(function () {
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Hero glow ---------- */

  function initHero(el) {
    if (el.dataset.qsReady) return;
    el.dataset.qsReady = '1';

    el.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      var r = el.getBoundingClientRect();
      el.style.setProperty('--qs-mx', (e.clientX - r.left) + 'px');
      el.style.setProperty('--qs-my', (e.clientY - r.top) + 'px');
    });
    el.addEventListener('pointerleave', function () {
      el.style.removeProperty('--qs-mx');
      el.style.removeProperty('--qs-my');
    });
  }

  /* ---------- Groups ---------- */

  function initGroups(root) {
    if (root.dataset.qsReady) return;
    root.dataset.qsReady = '1';

    var tabs = root.dataset.mode === 'tabs';
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-qs-group]'));
    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-qs-go]'));
    var body = root.querySelector('.qs-groups__body');
    if (!groups.length) return;

    function sticky() {
      return parseFloat(getComputedStyle(root).getPropertyValue('--qs-sticky')) || 0;
    }

    function setActive(id) {
      triggers.forEach(function (t) {
        var on = t.getAttribute('data-qs-go') === id;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      // keep the active mobile tab in view
      var tab = root.querySelector('.qs-groups__tab.is-active');
      if (tab && tab.parentNode.scrollWidth > tab.parentNode.clientWidth) {
        tab.parentNode.scrollTo({ left: tab.offsetLeft - 20, behavior: reduced ? 'auto' : 'smooth' });
      }
      if (tabs) {
        groups.forEach(function (g) { g.classList.toggle('is-active', g.id === id); });
      }
    }

    function scrollToEl(el) {
      var top = el.getBoundingClientRect().top + window.pageYOffset - sticky();
      window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });
    }

    function open(id, scroll) {
      var group = document.getElementById(id);
      if (!group || groups.indexOf(group) === -1) return;
      setActive(id);
      if (scroll) scrollToEl(tabs && body ? body : group);
    }

    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        var id = t.getAttribute('data-qs-go');
        open(id, true);
        if (history.replaceState) history.replaceState(null, '', '#' + id);
      });
    });

    // "Next group" buttons below each group's apps
    root.querySelectorAll('[data-qs-next]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('data-qs-next');
        if (!document.getElementById(id)) return;
        e.preventDefault();
        open(id, true);
        if (history.replaceState) history.replaceState(null, '', '#' + id);
      });
    });

    // Stacked mode: highlight the group currently on screen
    if (!tabs && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) setActive(en.target.id); });
      }, { rootMargin: '-40% 0px -55% 0px' });
      groups.forEach(function (g) { io.observe(g); });
    }

    // Deep links: #group-anchor or #app-anchor
    function fromHash() {
      var id = decodeURIComponent(location.hash.slice(1));
      if (!id) return;
      var el = document.getElementById(id);
      if (!el || !root.contains(el)) return;
      var group = el.closest('[data-qs-group]');
      if (!group) return;
      setActive(group.id);
      if (tabs) setTimeout(function () { scrollToEl(el); }, 0);
    }
    fromHash();
    window.addEventListener('hashchange', fromHash);

    root._qsOpen = function (el) {
      var group = el.closest('[data-qs-group]');
      if (group) setActive(group.id);
    };
  }

  /* ---------- Review carousel ---------- */

  function initCarousel(el) {
    if (el.dataset.qsReady) return;
    el.dataset.qsReady = '1';

    var slides = el.querySelectorAll('[data-qs-slide]');
    var dots = el.querySelectorAll('[data-qs-dot]');
    var status = el.querySelector('[data-qs-status]');
    var interval = (parseInt(el.dataset.interval, 10) || 0) * 1000;
    var i = 0, timer = null, held = false;
    if (slides.length < 2) return;

    function show(n) {
      i = (n + slides.length) % slides.length;
      for (var k = 0; k < slides.length; k++) {
        slides[k].setAttribute('aria-hidden', k === i ? 'false' : 'true');
        if (dots[k]) dots[k].classList.toggle('is-active', k === i);
      }
      if (status) status.textContent = (i + 1) + ' / ' + slides.length;
    }

    function start() {
      stop();
      if (!interval || reduced || held) return;
      timer = setInterval(function () { show(i + 1); }, interval);
    }
    function stop() { if (timer) clearInterval(timer); timer = null; }

    Array.prototype.forEach.call(dots, function (d, k) {
      d.addEventListener('click', function () { show(k); start(); });
    });
    ['mouseenter', 'focusin'].forEach(function (ev) {
      el.addEventListener(ev, function () { held = true; stop(); });
    });
    ['mouseleave', 'focusout'].forEach(function (ev) {
      el.addEventListener(ev, function () { held = false; start(); });
    });

    // only run while visible
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? start() : stop();
      }).observe(el);
    } else {
      start();
    }
  }

  /* ---------- Count-up ---------- */

  function initCount(el) {
    if (el.dataset.qsReady) return;
    el.dataset.qsReady = '1';

    var out = el.querySelector('[data-qs-count-value]');
    var target = parseInt(el.dataset.qsCount, 10);
    if (!out || !target || reduced || !('IntersectionObserver' in window)) return;

    var original = out.textContent;
    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      var t0 = null, dur = 1400;
      function step(ts) {
        if (!t0) t0 = ts;
        var p = Math.min((ts - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        out.textContent = Math.round(target * eased);
        if (p < 1) requestAnimationFrame(step);
        else out.textContent = original;
      }
      out.textContent = '0';
      requestAnimationFrame(step);
    }, { threshold: 0.4 });
    io.observe(el);
  }

  /* ---------- Boot ---------- */

  function init(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-qs-hero]'), initHero);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-qs-groups]'), initGroups);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-qs-carousel]'), initCarousel);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-qs-count]'), initCount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme Editor
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest && e.target.closest('[data-qs-groups]');
    if (root && root._qsOpen) root._qsOpen(e.target);
  });
})();
